import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { StatusChip, BureauChip, TimerBadge, OutcomeChip } from "@/components/StatusChip";
import CaseGuide from "@/components/CaseGuide";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, Sparkles, Download, Plus, FileText, Check, X } from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPES = [
  ["collection", "Collection"], ["late_payment", "Late Payment"], ["charge_off", "Charge-Off"],
  ["inquiry", "Hard Inquiry"], ["repossession", "Repossession"], ["bankruptcy", "Bankruptcy"],
  ["public_record", "Public Record"], ["other", "Other"],
];

export default function ClientDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const fileRef = useRef(null);
  const [docType, setDocType] = useState("credit_report");
  const [docBureau, setDocBureau] = useState("experian");
  const [uploading, setUploading] = useState(false);
  const [parsingId, setParsingId] = useState("");
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ bureau: "experian", creditor: "", type: "collection", account_number: "", balance: 0, date_reported: "", reason: "" });
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeForm, setDisputeForm] = useState({ bureau: "experian", template_id: "round1", item_ids: [] });
  const [note, setNote] = useState("");
  const [invOpen, setInvOpen] = useState(false);
  const [invForm, setInvForm] = useState({ label: "", amount: "", kind: "monthly" });
  const [cashRef, setCashRef] = useState({ id: "", reference: "" });
  const [tab, setTab] = useState("items");

  const load = () => api.get(`/clients/${cid}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/templates").then((r) => setTemplates(r.data)); }, [cid]);

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading client…</div>;
  const { client, documents, items, disputes, invoices, notes, agreement } = data;
  const hasFtc = documents.some((d) => d.type === "ftc_report");

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file first");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("doc_type", docType);
    fd.append("bureau", (docType === "credit_report" || docType === "bureau_response") ? docBureau : "");
    setUploading(true);
    try {
      const r = await api.post(`/clients/${cid}/documents`, fd);
      const res = r.data.ai_result;
      if (docType === "bureau_response" && res) toast.success(res.matched > 0 ? `AI read the bureau letter — ${res.matched} item(s) auto-marked` : "Letter saved — no open dispute items matched", { description: res.summary });
      else if (r.data.ai_error) toast.error(`Uploaded, but AI couldn't read it: ${r.data.ai_error}`);
      else toast.success("Document uploaded");
      fileRef.current.value = "";
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const parse = async (docId) => {
    setParsingId(docId);
    try {
      const r = await api.post(`/documents/${docId}/parse`);
      toast.success(`AI flagged ${r.data.items_created} negative item${r.data.items_created === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setParsingId(""); }
  };

  const setItemStatus = async (itemId, status) => {
    try { await api.patch(`/items/${itemId}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/clients/${cid}/items`, { ...itemForm, balance: Number(itemForm.balance) || 0 });
      toast.success("Item added");
      setNewItemOpen(false);
      setItemForm({ bureau: "experian", creditor: "", type: "collection", account_number: "", balance: 0, date_reported: "", reason: "" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const confirmedForBureau = items.filter((i) => i.bureau === disputeForm.bureau && ["confirmed", "flagged"].includes(i.status));

  const createDispute = async () => {
    if (disputeForm.item_ids.length === 0) return toast.error("Select at least one item");
    try {
      const r = await api.post("/disputes", { client_id: cid, ...disputeForm });
      toast.success("Dispute letter drafted");
      setDisputeOpen(false);
      navigate(`/admin/disputes/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    await api.post(`/clients/${cid}/notes`, { text: note });
    setNote("");
    load();
  };

  const addInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.post("/billing/invoices", { client_id: cid, label: invForm.label, amount: Number(invForm.amount), kind: invForm.kind });
      toast.success("Invoice created");
      setInvOpen(false);
      setInvForm({ label: "", amount: "", kind: "monthly" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const recordCashApp = async () => {
    try {
      await api.post(`/billing/invoices/${cashRef.id}/cashapp`, { reference: cashRef.reference });
      toast.success("CashApp payment recorded");
      setCashRef({ id: "", reference: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const moveStatus = async (status) => {
    await api.patch(`/clients/${cid}`, { status });
    toast.success(`Moved to ${status}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="client-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/clients" className="text-xs text-slate-400 hover:text-slate-600">← All clients</Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-1">{client.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <StatusChip status={client.status} />
            <StatusChip status={client.intake_completed ? "confirmed" : "pending"} testId="intake-chip" />
            <StatusChip status={client.agreement_signed ? "signed" : "pending"} testId="agreement-chip" />
            <StatusChip status={client.setup_fee_paid ? "paid" : "pending"} testId="setup-fee-chip" />
          </div>
        </div>
        <Select value={client.status} onValueChange={moveStatus}>
          <SelectTrigger className="w-44" data-testid="client-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="lead">Lead</SelectItem>
            <SelectItem value="onboarded">Onboarded</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <CaseGuide data={data} onNavigate={setTab} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="client-detail-tabs">
          <TabsTrigger value="items" data-testid="tab-items">Negative Items</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="tab-disputes">Disputes</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4 mt-4">
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4" data-testid="report-upload-dropzone">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label>Document type</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="w-44 mt-1" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit_report">Credit report (PDF)</SelectItem>
                    <SelectItem value="id_proof">Government ID</SelectItem>
                    <SelectItem value="address_proof">Proof of address</SelectItem>
                    <SelectItem value="ssn_card">SSN card</SelectItem>
                    <SelectItem value="ftc_report">FTC Identity Theft Report</SelectItem>
                    <SelectItem value="bureau_response">Bureau response letter</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(docType === "credit_report" || docType === "bureau_response") && (
                <div>
                  <Label>Bureau</Label>
                  <Select value={docBureau} onValueChange={setDocBureau}>
                    <SelectTrigger className="w-40 mt-1" data-testid="doc-bureau-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="experian">Experian</SelectItem>
                      <SelectItem value="transunion">TransUnion</SelectItem>
                      <SelectItem value="equifax">Equifax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Input type="file" ref={fileRef} data-testid="doc-file-input" className="w-64" accept={docType === "credit_report" ? "application/pdf" : undefined} />
              <Button onClick={upload} disabled={uploading} data-testid="doc-upload-btn" variant="outline">
                <UploadCloud className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </div>

          {documents.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white">
              <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Uploaded documents</div>
              <div className="divide-y divide-slate-100">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{d.filename}</div>
                        <div className="text-xs text-slate-400">{d.type.replace("_", " ")} {d.bureau ? `· ${d.bureau}` : ""} · {fmtDate(d.created_at)} · {d.uploaded_by}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {d.parsed && <span className="text-xs text-emerald-700 font-medium">Parsed</span>}
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
                            window.open(URL.createObjectURL(r.data), "_blank");
                          } catch (e2) { toast.error(errMsg(e2)); }
                        }}
                        data-testid={`doc-download-${d.id.slice(0, 8)}`} className="text-slate-500 hover:text-slate-800"><Download className="h-4 w-4" /></a>
                      {d.type === "credit_report" && (
                        <Button size="sm" variant="outline" disabled={parsingId === d.id} onClick={() => parse(d.id)} data-testid={`parse-report-btn-${d.id.slice(0, 8)}`}>
                          <Sparkles className="h-3.5 w-3.5 mr-1.5 text-amber-500" />
                          {parsingId === d.id ? "Analyzing…" : d.parsed ? "Re-analyze" : "Analyze with AI"}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500">Negative items ({items.length})</span>
              <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="add-item-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add manually</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">Add negative item</DialogTitle></DialogHeader>
                  <form onSubmit={addItem} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Bureau</Label>
                        <Select value={itemForm.bureau} onValueChange={(v) => setItemForm({ ...itemForm, bureau: v })}>
                          <SelectTrigger className="mt-1" data-testid="item-bureau-select"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="experian">Experian</SelectItem><SelectItem value="transunion">TransUnion</SelectItem><SelectItem value="equifax">Equifax</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div><Label>Type</Label>
                        <Select value={itemForm.type} onValueChange={(v) => setItemForm({ ...itemForm, type: v })}>
                          <SelectTrigger className="mt-1" data-testid="item-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{ITEM_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label>Creditor / furnisher</Label><Input required value={itemForm.creditor} onChange={(e) => setItemForm({ ...itemForm, creditor: e.target.value })} data-testid="item-creditor-input" className="mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Account # (masked ok)</Label><Input value={itemForm.account_number} onChange={(e) => setItemForm({ ...itemForm, account_number: e.target.value })} data-testid="item-account-input" className="mt-1" /></div>
                      <div><Label>Balance ($)</Label><Input type="number" step="0.01" value={itemForm.balance} onChange={(e) => setItemForm({ ...itemForm, balance: e.target.value })} data-testid="item-balance-input" className="mt-1" /></div>
                    </div>
                    <div><Label>Dispute basis</Label>
                      <Select value={itemForm.reason || "custom"} onValueChange={(v) => setItemForm({ ...itemForm, reason: v === "custom" ? "" : v })}>
                        <SelectTrigger className="mt-1" data-testid="dispute-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Metro 2 compliance failure — furnisher could not verify">Metro 2: Failure to verify</SelectItem>
                          <SelectItem value="Identity theft / unauthorized account or inquiry">Identity theft / unauthorized</SelectItem>
                          <SelectItem value="Inaccurate date of last activity (re-aging)">Inaccurate DOLA (re-aging)</SelectItem>
                          <SelectItem value="Obsolete — exceeds 7-year reporting limit">Obsolete (past 7-year limit)</SelectItem>
                          <SelectItem value="Balance or status reported inaccurately">Inaccurate balance/status</SelectItem>
                          <SelectItem value="custom">Custom reason…</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {!ITEM_TYPES.some(() => false) && (
                      <Input placeholder="Reason detail" value={itemForm.reason} onChange={(e) => setItemForm({ ...itemForm, reason: e.target.value })} data-testid="item-reason-input" />
                    )}
                    <Button type="submit" data-testid="item-submit-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Add item</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-flagged-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    {["Creditor", "Bureau", "Type", "Balance", "Basis", "Status", "Review"].map((h) => (
                      <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id} className="border-b border-slate-100 align-top">
                      <td className="py-2.5 px-3.5 font-medium text-slate-800">{i.creditor}
                        {i.client_flag === "not_mine" && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid={`not-mine-badge-${i.id.slice(0, 8)}`}>Client: not mine</span>}
                        <div className="text-xs text-slate-400 font-normal">{i.account_number}</div></td>
                      <td className="py-2.5 px-3.5"><BureauChip bureau={i.bureau} /></td>
                      <td className="py-2.5 px-3.5 text-slate-600 capitalize">{i.type.replace("_", " ")}</td>
                      <td className="py-2.5 px-3.5 text-slate-600">{i.balance ? money(i.balance) : "—"}</td>
                      <td className="py-2.5 px-3.5 text-xs text-slate-500 max-w-56">{i.reason}</td>
                      <td className="py-2.5 px-3.5"><StatusChip status={i.status} /></td>
                      <td className="py-2.5 px-3.5">
                        {["flagged", "confirmed"].includes(i.status) ? (
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => setItemStatus(i.id, "confirmed")} data-testid={`confirm-item-${i.id.slice(0, 8)}`}><Check className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setItemStatus(i.id, "dismissed")} data-testid={`dismiss-item-${i.id.slice(0, 8)}`}><X className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No items yet. Upload a credit report and run AI analysis, or add items manually.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="disputes" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Dialog open={disputeOpen} onOpenChange={(o) => { setDisputeOpen(o); if (o && hasFtc) setDisputeForm((f) => ({ ...f, template_id: "fraud_block" })); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-dispute-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white"><Plus className="h-4 w-4 mr-2" /> New Dispute Letter</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">Generate dispute letter</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {hasFtc && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="ftc-report-banner">
                      This client has an FTC Identity Theft Report on file — the §605B block letter is pre-selected and the report will be attached to the PDF automatically.
                    </div>
                  )}
                  {!hasFtc && items.some((i) => i.client_flag === "not_mine") && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="fraud-no-ftc-hint">
                      The client flagged item(s) as "not mine" but hasn't uploaded an FTC Identity Theft Report yet. Ask them to file at IdentityTheft.gov so you can use the §605B block letter.
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Bureau</Label>
                      <Select value={disputeForm.bureau} onValueChange={(v) => setDisputeForm({ ...disputeForm, bureau: v, item_ids: [] })}>
                        <SelectTrigger className="mt-1" data-testid="dispute-bureau-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="experian">Experian</SelectItem><SelectItem value="transunion">TransUnion</SelectItem><SelectItem value="equifax">Equifax</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Template</Label>
                      <Select value={disputeForm.template_id} onValueChange={(v) => setDisputeForm({ ...disputeForm, template_id: v })}>
                        <SelectTrigger className="mt-1" data-testid="dispute-template-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Items to include ({confirmedForBureau.length} available for this bureau)</Label>
                    <div className="mt-2 max-h-52 overflow-y-auto space-y-2 rounded-md border border-slate-200 p-3">
                      {confirmedForBureau.length === 0 && <div className="text-xs text-slate-400">No confirmed items for this bureau. Confirm items in the Negative Items tab first.</div>}
                      {confirmedForBureau.map((i) => (
                        <label key={i.id} className="flex items-start gap-2 text-sm text-slate-700">
                          <Checkbox data-testid="flagged-item-checkbox"
                            checked={disputeForm.item_ids.includes(i.id)}
                            onCheckedChange={(v) => setDisputeForm({
                              ...disputeForm,
                              item_ids: v ? [...disputeForm.item_ids, i.id] : disputeForm.item_ids.filter((x) => x !== i.id),
                            })} />
                          <span>{i.creditor} <span className="text-xs text-slate-400">· {i.type.replace("_", " ")} {i.balance ? `· ${money(i.balance)}` : ""}</span>
                            {i.client_flag === "not_mine" && <span className="ml-1.5 text-[10px] font-semibold uppercase text-rose-700">Not mine</span>}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button onClick={createDispute} data-testid="generate-dispute-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Generate letter</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  {["Dispute", "Bureau", "Round", "Status", "30-Day Clock", "Tracking", "Outcome"].map((h) => (
                    <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {disputes.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                    <td className="py-2.5 px-3.5">
                      <Link to={`/admin/disputes/${d.id}`} className="font-medium text-blue-700 hover:underline" data-testid={`dispute-row-${d.id.slice(0, 8)}`}>{d.template_name}</Link>
                      <div className="text-xs text-slate-400">{fmtDate(d.created_at)}</div>
                    </td>
                    <td className="py-2.5 px-3.5"><BureauChip bureau={d.bureau} /></td>
                    <td className="py-2.5 px-3.5 text-slate-600">R{d.round}</td>
                    <td className="py-2.5 px-3.5"><StatusChip status={d.status} /></td>
                    <td className="py-2.5 px-3.5"><TimerBadge daysLeft={d.days_left} status={d.status} /></td>
                    <td className="py-2.5 px-3.5 text-xs font-mono text-slate-500">{d.tracking_number || "—"}</td>
                    <td className="py-2.5 px-3.5"><OutcomeChip outcome={d.outcome} /></td>
                  </tr>
                ))}
                {disputes.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400">No disputes yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2 text-sm">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Contact & identity</div>
              <div><span className="text-slate-400">Email:</span> {client.email}</div>
              <div><span className="text-slate-400">Phone:</span> {client.phone || "—"}</div>
              <div><span className="text-slate-400">DOB:</span> {client.dob || "—"}</div>
              <div><span className="text-slate-400">SSN:</span> {client.ssn_last4 ? `•••-••-${client.ssn_last4}` : "—"}</div>
              <div><span className="text-slate-400">Address:</span> {client.address?.street ? `${client.address.street}, ${client.address.city}, ${client.address.state} ${client.address.zip}` : "—"}</div>
              <div><span className="text-slate-400">Assigned:</span> {client.assigned_to || "Unassigned"}</div>
              <div><span className="text-slate-400">Goals:</span> {client.intake?.goals || "—"}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="staff-score-card">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">Score journey</div>
              <ScoreEdit client={client} onSaved={load} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Dialog open={invOpen} onOpenChange={setInvOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="new-invoice-btn"><Plus className="h-4 w-4 mr-2" /> New Invoice</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Create invoice</DialogTitle></DialogHeader>
                <form onSubmit={addInvoice} className="space-y-3">
                  <div><Label>Description</Label><Input required value={invForm.label} onChange={(e) => setInvForm({ ...invForm, label: e.target.value })} data-testid="invoice-label-input" className="mt-1" placeholder="Monthly service — June 2026" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Amount ($)</Label><Input type="number" step="0.01" required value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} data-testid="invoice-amount-input" className="mt-1" /></div>
                    <div><Label>Kind</Label>
                      <Select value={invForm.kind} onValueChange={(v) => setInvForm({ ...invForm, kind: v })}>
                        <SelectTrigger className="mt-1" data-testid="invoice-kind-select"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="setup">Setup</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" data-testid="invoice-submit-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Create invoice</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                {["Invoice", "Amount", "Status", "Method", "Reference", ""].map((h) => (
                  <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-b border-slate-100">
                    <td className="py-2.5 px-3.5 font-medium text-slate-800">{i.label}<div className="text-xs text-slate-400 font-normal">{fmtDate(i.created_at)}</div></td>
                    <td className="py-2.5 px-3.5 text-slate-700">{money(i.amount)}</td>
                    <td className="py-2.5 px-3.5"><StatusChip status={i.status} testId="invoice-status-pill" /></td>
                    <td className="py-2.5 px-3.5 text-slate-600 capitalize">{i.method || "—"}</td>
                    <td className="py-2.5 px-3.5 text-xs font-mono text-slate-500">{i.reference || "—"}</td>
                    <td className="py-2.5 px-3.5">
                      {i.status === "pending" && (
                        cashRef.id === i.id ? (
                          <div className="flex gap-1.5">
                            <Input placeholder="$Cashtag / ref" value={cashRef.reference} onChange={(e) => setCashRef({ ...cashRef, reference: e.target.value })} className="h-8 w-36 text-xs" data-testid="cashapp-ref-input" />
                            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={recordCashApp} data-testid="cashapp-confirm-btn">Save</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setCashRef({ id: i.id, reference: "" })} data-testid="record-cashapp-btn">Record CashApp</Button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No invoices.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="space-y-4 mt-4">
          <form onSubmit={addNote} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
            <Textarea placeholder="Add a case note (call summary, client request, next steps)…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
            <Button type="submit" variant="outline" data-testid="note-submit-btn">Add note</Button>
          </form>
          <div className="space-y-3">
            {notes.map((n) => (
              <div key={n.id} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-sm text-slate-700">{n.text}</div>
                <div className="text-xs text-slate-400 mt-2">{n.author_name} · {new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
            {notes.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No notes yet.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ScoreEdit({ client, onSaved }) {
  const [s, setS] = useState(client.scores || { start: "", current: "", goal: 720 });
  const save = async () => {
    await api.patch(`/clients/${client.id}`, { scores: { start: s.start ? Number(s.start) : null, current: s.current ? Number(s.current) : null, goal: Number(s.goal) || 720 } });
    toast.success("Scores updated");
    onSaved();
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {[["start", "Start"], ["current", "Current"], ["goal", "Goal"]].map(([k, l]) => (
          <div key={k}><Label>{l}</Label><Input type="number" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1" data-testid={`score-${k}-input`} /></div>
        ))}
      </div>
      {s.start && s.current && (
        <div className="text-sm text-emerald-700 font-medium">+{Number(s.current) - Number(s.start)} points since start</div>
      )}
      <Button size="sm" variant="outline" onClick={save} data-testid="score-save-btn">Save scores</Button>
    </div>
  );
}
